/* eslint-disable consistent-return */
import BluebirdPromise from 'bluebird';
import { get, has, includes, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Prompt, Redirect, Route, withRouter } from 'react-router-dom';
import { getFormValues, isDirty, SubmissionError } from 'redux-form';
import { LEARNING_ASSET_DURATIONS } from '../../../shared/Constants';

import PageContent from '../../../shared/components/PageContent';
import CollectionContentForm from '../forms/Admin/CollectionForm/CollectionContentForm';
import CollectionSummaryForm from '../forms/Admin/CollectionForm/CollectionSummaryForm';

import { RuntimeContext } from '../../../../common/RuntimeContext';
import {
    getCollection,
    getLearningFramework,
    getManyFilesMetadata,
    getManyLearningAssets,
    postCollection,
    putCollection
} from '../../../shared/actions';
import MenuTabs from '../../../shared/components/MenuTabs';
import Page from '../../../shared/components/Page';
import PageHeader from '../../../shared/components/PageHeader';
import { removeHTMLtagsAndDoubleQuotes } from '../../../shared/utils/StringUtils';

class AddOrEditCollectionV2 extends Component {
    static propTypes = {
        getCollection: PropTypes.func.isRequired,
        postCollection: PropTypes.func.isRequired,
        putCollection: PropTypes.func.isRequired,
        getLearningFramework: PropTypes.func.isRequired,
        getManyLearningAssets: PropTypes.func.isRequired,
        getManyFilesMetadata: PropTypes.func,
        match: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        loggedInUser: PropTypes.object.isRequired,
        learningAssetsById: PropTypes.object,
        currentCollection: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        location: PropTypes.object,
        dirty: PropTypes.bool,
        getCurrentCollectionFormValues: PropTypes.func
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            mediaMetadata: [],
            menuTabs: this._buildMenuTabsArray()
        };

        this.isSubmitting = false;
    }

    componentDidMount() {
        const {
            match,
            getCollection,
            getManyLearningAssets,
            getManyFilesMetadata,
            getLearningFramework,
            location
        } = this.props;
        this.setState({
            isLoading: true
        });

        const promises = [];
        promises.push(
            getLearningFramework({ pathsContext: this.context.pathsWeb }).then(({ dimensions }) => {
                const formattedDimensions = dimensions.map((dimension) => {
                    const formattedDimension = { ...dimension };
                    formattedDimension.name = removeHTMLtagsAndDoubleQuotes(dimension.name);
                    return formattedDimension;
                });
                this.setState({
                    verbDimensions: formattedDimensions
                });
            })
        );
        promises.push(getManyLearningAssets({ pathsContext: this.context.pathsWeb }));
        promises.push(
            getManyFilesMetadata({ pathsContext: this.context.pathsWeb }).then((mediaMetadata) =>
                this.setState({ mediaMetadata })
            )
        );
        if (match.params.id) {
            promises.push(getCollection({ collectionId: match.params.id, getFullProjection: true, pathsContext: this.context.pathsWeb }));
        }
        return BluebirdPromise.all(promises).then(() => {
            const copiedCollection = get(location, 'state.copiedCollection', null);
            const newState = { isLoading: false };
            if (copiedCollection) {
                newState.copiedCollection = copiedCollection;
            }
            this.setState(newState);
        });
    }

    componentDidUpdate(prevProps) {
        const { match, getCollection } = this.props;
        const promises = [];
        if (match.params.id !== prevProps.match.params.id) {
            promises.push(getCollection({ collectionId: match.params.id, pathsContext: this.context.pathsWeb }));
        }

        if (match.path !== prevProps.match.path) {
            this.setState({
                menuTabs: this._buildMenuTabsArray()
            });
        }

        return BluebirdPromise.all(promises);
    }

    onSubmit = ({ values, options = {}, associatedDimensionsUpdatePromise }) => {
        const {
            currentCollection,
            putCollection,
            postCollection,
            loggedInUser,
            learningAssetsById,
            match,
            getCurrentCollectionFormValues
        } = this.props;

        let newCollection = Object.assign({}, currentCollection, values);
        const savedCollection = getCurrentCollectionFormValues();

        if (match?.params?.section === 'content') {
            newCollection = Object.assign({}, currentCollection, { contentIds: savedCollection.contentIds });
        }

        if (newCollection.type !== 'COLLAB' && newCollection.isContentProgressionLocked) {
            delete newCollection.isContentProgressionLocked;
        }
        let durationInMinutes = 0;
        const contentIds = newCollection.contentIds || [];
        const durationDict = LEARNING_ASSET_DURATIONS();
        for (let i = 0; i < contentIds.length; i += 1) {
            const asset = learningAssetsById[contentIds[i].id];
            if (asset?.durationId) {
                const assetDuration = durationDict[asset.durationId] && durationDict[asset.durationId].inMinutes;
                durationInMinutes += assetDuration;
            }
        }
        newCollection.durationInMinutes = durationInMinutes;
        let submitPromise = BluebirdPromise.resolve();

        let isNew = false;
        const duplicatePermalinkError = new SubmissionError({
            _error: `This permalink ${newCollection.permalink} is already in use!`
        });
        if (!has(newCollection, '_id')) {
            isNew = true;
            newCollection.author = {
                _id: loggedInUser._id,
                firstName: loggedInUser.firstName,
                lastName: loggedInUser.lastName
            };

            submitPromise = postCollection({ body: newCollection, pathsContext: this.context.pathsWeb }).catch(
                (err) => {
                    if (err.body && err.body.name === 'DuplicateItemFound') {
                        throw duplicatePermalinkError;
                    }
                }
            );
        } else {
            submitPromise = putCollection({
                collectionId: newCollection._id,
                body: newCollection,
                options,
                pathsContext: this.context.pathsWeb
            }).catch((err) => {
                if (err.body && err.body.name === 'DuplicateItemFound') {
                    throw duplicatePermalinkError;
                }
            });
        }

        if (isNew) {
            this.isSubmitting = true;
            return submitPromise.then((result) => {
                this.props.history.push(`/editcollection/${result._id}`);
                this.isSubmitting = false;
            });
        }

        return submitPromise
            .then(() => {
                if (typeof associatedDimensionsUpdatePromise === 'function') {
                    return associatedDimensionsUpdatePromise().catch((err) => {
                        if (err) {
                            throw new SubmissionError({
                                _error: `Something went wrong while updating collection's associated custom framework`
                            });
                        }
                    });
                }
            });
    };

    handleCancel = () => {
        this.props.history.push('/managecollections');
    };

    _getCollectionTabPath = ({ tabName, isNew }) => {
        const { match } = this.props;
        if (isNew) {
            return `/newcollection/${tabName}`;
        }
        return `/editcollection/${match.params.id}/${tabName}`;
    };

    _buildMenuTabsArray = () => {
        const {
            match: { path }
        } = this.props;
        const isNew = includes(path, 'new');
        return [
            {
                name: 'Summary',
                icon: 'fa-cog',
                tabId: 'summary',
                title: 'Summary',
                path: this._getCollectionTabPath({ tabName: 'summary', isNew })
            },
            {
                name: 'Content',
                icon: 'fa-layer-group',
                tabId: 'content',
                title: 'Content',
                path: this._getCollectionTabPath({ tabName: 'content', isNew })
            }
        ];
    };

    render() {
        const {
            currentCollection = {},
            match: {
                path,
                params: { section = '' }
            },
            match,
            history,
            isVerbAdmin,
            isAccountOwner,
            loggedInUser,
            location,
            dirty
        } = this.props;
        const {
            menuTabs,
            isLoading,
            verbDimensions,
            copiedCollection
        } = this.state;
        const isNew = includes(path, 'new');
        const components = {
            summary: { component: CollectionSummaryForm, title: `${currentCollection.name}: Summary` },
            content: { component: CollectionContentForm, title: `${currentCollection.name}: Content` }
        };

        const activeMenuTabTitle = get(components[section], 'title', '');
        const title = isNew ? 'Create New Path' : activeMenuTabTitle;

        if (section && !components[section]) {
            return <Redirect to={`${match.url}/summary`} />;
        }

        const routes = menuTabs.map((tab) => {
            if (!tab.isHidden) {
                return (
                    <Route
                        key={tab.path}
                        path={tab.path}
                        exact={true}
                        render={() => {
                            const ActiveMenuTabComponent = components[tab.tabId].component;
                            return (
                                <ActiveMenuTabComponent
                                    isLoading={isLoading}
                                    history={history}
                                    currentCollection={currentCollection}
                                    onSubmit={this.onSubmit}
                                    onCancel={this.handleCancel}
                                    isVerbAdmin={isVerbAdmin}
                                    isAccountOwner={isAccountOwner}
                                    loggedInUser={loggedInUser}
                                    mediaMetadata={this.state.mediaMetadata}
                                    match={match}
                                    verbDimensions={verbDimensions}
                                    copiedCollection={copiedCollection}
                                />
                            );
                        }}
                    />
                );
            }
            return null;
        });
        const renderedSideNavItems = <MenuTabs menuItems={menuTabs} initialTab={section} />;

        if (!section) {
            return (
                <Redirect
                    to={{
                        pathname: `${match.url}/summary`,
                        state: location.state // forward location state
                    }}
                />
            );
        }
        return (
            <Page className={'add-or-edit-collection-v2'}>
                <Prompt
                    when={dirty && !this.isSubmitting}
                    message={'You have unsaved changes, are you sure you want to leave without saving changes?'}
                />
                <div className={'col-md-2 verticalSubMenuList'}>{renderedSideNavItems}</div>
                <div className={'col-md-10'}>
                    <PageContent className={'col-12 ms-auto container'}>
                        <PageHeader
                            title={title}
                            bottomBorder={true}
                            hideBackButton={true}
                            className={'collection-form-header'}
                        />
                        {routes}
                    </PageContent>
                </div>
            </Page>
        );
    }
}

export const mapStateToProps = (state, ownProps) => {
    const {
        auth: { isAuthenticated, loggedInUser, isContentAuthor, isVerbAdmin },
        collections: { byId }
    } = state;
    const collectionId = ownProps.match.params.id;
    let currentCollection;
    if (collectionId) {
        currentCollection = byId[collectionId];
    } else {
        currentCollection = {};
    }
    const allAssetsById = get(state, 'learningAssets.byId', {});

    const getCurrentCollectionFormValues = () => getFormValues('Collection')(state);

    return {
        learningAssetsById: allAssetsById,
        isAuthenticated,
        isContentAuthor,
        isVerbAdmin,
        currentCollection,
        getCurrentCollectionFormValues,
        loggedInUser,
        dirty: isDirty('Collection')(state)
    };
};

const mapDispatchToProps = (dispatch) => ({
    postCollection: (params) => dispatch(postCollection(params)),
    putCollection: (params) => dispatch(putCollection(params)),
    getCollection: (params) => dispatch(getCollection(params)),
    getManyLearningAssets: (params) => dispatch(getManyLearningAssets(params)),
    getManyFilesMetadata: (params) => getManyFilesMetadata(params),
    getLearningFramework: (params) => dispatch(getLearningFramework(params))
});

export const AddOrEditCollectionV2Component = AddOrEditCollectionV2;
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AddOrEditCollectionV2));
